import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Filter } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TaskCard from "@/components/TaskCard";
import TaskFormDialog from "@/components/TaskFormDialog";
import TaskStatusDialog from "@/components/TaskStatusDialog";
import TaskRemarksDialog from "@/components/TaskRemarksDialog";
import { isOverdue } from "@/lib/utils-task";

export default function TasksPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [scope, setScope] = useState("all"); // 'all' | 'mine' — for Admin/Manager
  const [formOpen, setFormOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [remarksTask, setRemarksTask] = useState(null);
  const [editing, setEditing] = useState(null);
  const [statusTask, setStatusTask] = useState(null);
  const [deleteTask, setDeleteTask] = useState(null);

  const canCreate = user.role === "Admin" || user.role === "Manager";

  const load = useCallback(async () => {
    try {
      const [tRes, uRes] = await Promise.all([
        api.get("/tasks"),
        canCreate ? api.get("/users") : Promise.resolve({ data: [] }),
      ]);
      setTasks(tRes.data);
      setAssignees(uRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [canCreate]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tasks
      .filter((t) => (scope === "mine" ? t.assignee_id === user.id : true))
      .filter((t) => (statusFilter === "all" ? true : t.status === statusFilter))
      .filter((t) => (priorityFilter === "all" ? true : t.priority === priorityFilter))
      .filter((t) =>
        !q
          ? true
          : t.title.toLowerCase().includes(q) ||
            (t.description || "").toLowerCase().includes(q) ||
            (t.assignee_name || "").toLowerCase().includes(q)
      );
  }, [tasks, scope, statusFilter, priorityFilter, search, user]);

  const groups = useMemo(() => {
    const g = { overdue: [], Pending: [], "In-Progress": [], Done: [] };
    filtered.forEach((t) => {
      if (t.status !== "Done" && isOverdue(t)) g.overdue.push(t);
      else g[t.status].push(t);
    });
    return g;
  }, [filtered]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setFormOpen(true);
  };
  const openStatus = (t) => {
    setStatusTask(t);
    setStatusDialogOpen(true);
  };
  const confirmDelete = async () => {
    if (!deleteTask) return;
    try {
      await api.delete(`/tasks/${deleteTask.id}`);
      toast.success("Task deleted");
      setDeleteTask(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return <div className="label-eyebrow">Loading…</div>;

  const Section = ({ title, items, testid, tone }) => (
    <section className="space-y-3" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className={`label-eyebrow ${tone || ""}`}>{title}</div>
        <div className="text-xs text-slate-500">({items.length})</div>
        <div className="flex-1 border-t border-dashed border-slate-200" />
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-md p-6 text-center">
          Nothing here.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((t) => (
            <TaskCard
              key={t.id}
              task={t}
              currentUser={user}
              onUpdateStatus={openStatus}
              onEdit={openEdit}
              onDelete={setDeleteTask}
              onViewRemarks={setRemarksTask}
            />
          ))}
        </div>
      )}
    </section>
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="label-eyebrow">Tasks</div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-black tracking-tight text-slate-900">
            {user.role === "Employee" ? "My tasks" : "Task board"}
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            {user.role === "Employee"
              ? "Update statuses as work progresses. Overdue items are flagged."
              : "Create, assign and track task progress across your team."}
          </p>
        </div>
        {canCreate && (
          <Button
            onClick={openCreate}
            className="bg-slate-900 hover:bg-slate-800 text-white transition-transform hover:-translate-y-[1px]"
            data-testid="create-task-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            New task
          </Button>
        )}
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 bg-white border border-slate-200 rounded-md p-3">
        {canCreate && (
          <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-slate-50" data-testid="tasks-scope-toggle">
            <button
              type="button"
              onClick={() => setScope("all")}
              className={`px-3 py-1.5 text-xs font-semibold rounded-[5px] transition-colors ${
                scope === "all" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid="tasks-scope-all"
            >
              All tasks
            </button>
            <button
              type="button"
              onClick={() => setScope("mine")}
              className={`px-3 py-1.5 text-xs font-semibold rounded-[5px] transition-colors ${
                scope === "mine" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid="tasks-scope-mine"
            >
              My tasks
            </button>
          </div>
        )}
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            className="pl-9"
            placeholder="Search by title, description or assignee"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="tasks-search-input"
          />
        </div>
        <div className="flex gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]" data-testid="tasks-status-filter">
              <Filter className="h-3.5 w-3.5 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="In-Progress">In Progress</SelectItem>
              <SelectItem value="Done">Done</SelectItem>
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-[150px]" data-testid="tasks-priority-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              <SelectItem value="Low">Low</SelectItem>
              <SelectItem value="Medium">Medium</SelectItem>
              <SelectItem value="High">High</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-10">
        <Section title="Overdue" items={groups.overdue} testid="tasks-section-overdue" tone="text-rose-600" />
        <Section title="Pending" items={groups.Pending} testid="tasks-section-pending" />
        <Section title="In Progress" items={groups["In-Progress"]} testid="tasks-section-in-progress" />
        <Section title="Done" items={groups.Done} testid="tasks-section-done" />
      </div>

      <TaskFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={load}
        task={editing}
        assignees={assignees}
      />
      <TaskStatusDialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        task={statusTask}
        onSaved={load}
      />
      <TaskRemarksDialog
        open={!!remarksTask}
        onClose={() => setRemarksTask(null)}
        task={remarksTask}
      />
      <AlertDialog open={!!deleteTask} onOpenChange={(o) => !o && setDeleteTask(null)}>
        <AlertDialogContent data-testid="task-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this task?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The task will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="task-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="task-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
